import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import httpClient from '~/lib/http-client';

/**
 * Desktop → Linear OAuth handoff. Dunea Desktop opens this page with a
 * loopback redirect_uri; once the browser session is signed in, one click
 * sends the user through Linear's consent screen. The API's callback stores
 * the (encrypted) token on the account and bounces a one-time code back to
 * the app's loopback, whose done page offers "Open Dunea Desktop".
 */
export default function DesktopLinearPage() {
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const redirectUri = params.get('redirect_uri') ?? '';
  const state = params.get('state') ?? '';

  const redirectValid = useMemo(
    () => /^http:\/\/(127\.0\.0\.1|localhost):\d{2,5}\/callback$/.test(redirectUri),
    [redirectUri],
  );

  const authorize = async () => {
    setBusy(true);
    setError(null);

    try {
      const { data } = await httpClient.post<{ authorizeUrl: string }>('/api/integrations/linear/start', {
        redirectUri,
        state,
      });
      window.location.href = data.authorizeUrl;
    } catch (e) {
      console.error(e);
      setError('Something went wrong — please try again.');
      setBusy(false);
    }
  };

  return (
    <div className="ui-root min-h-dvh bg-page flex items-center justify-center p-5 sm:p-8">
      <div className="w-full max-w-[440px] text-center">
        <div className="ui-caption text-accent mb-3">Dunea Desktop</div>
        <h1 className="ui-serif text-[32px] sm:text-[40px] leading-[1.05] text-primary m-0 mb-3">
          Connect <span className="italic text-accent">Linear</span>
        </h1>

        {!redirectValid ? (
          <p className="text-[13px] text-danger leading-relaxed">
            This connect link is malformed — launch it again from Dunea Desktop → Settings → Connections.
          </p>
        ) : (
          <>
            <p className="text-[13.5px] text-secondary leading-[1.65] mb-8">
              Your issues appear in the app's Create-from picker, and branches auto-link back to Linear. You'll approve
              access on Linear's consent screen next.
            </p>
            <button
              className="ui-btn-accent w-full !py-3 !text-[14px]"
              disabled={busy}
              onClick={() => void authorize()}
            >
              {busy ? 'Authorizing…' : 'Authorize'}
            </button>
            {error && <p className="text-[12px] text-danger mt-4">{error}</p>}
          </>
        )}
      </div>
    </div>
  );
}
